// How a war screen gets its presentation tuning (docs/ROADMAP.md D33, D34). Players' builds run the committed values
// (@taxwar/tuning values.json, fixedTuning). The tuner page (src/tune, served only by tools/tuner) runs a live link: it
// retunes the war on screen as its controls move, previews the status arcs and either palette, and moves the camera to
// zoom presets.

import type { PaletteMode, TuningValues } from '@taxwar/tuning'

/**
 * What the status arcs show (render/network/CityStatusLayer.ts): each city's own state ('live', all players ever see),
 * or — in the tuner only — every city damaged, or every city being captured, so the arcs can be tuned without a battle.
 */
export const CITY_PREVIEWS = ['live', 'damaged', 'capturing'] as const
export type CityPreview = (typeof CITY_PREVIEWS)[number]

/** Camera presets the tuner offers: the war's opening framing, close in, and the closest zoom. */
export const ZOOM_PRESETS = ['opening', 'close', 'closest'] as const
export type ZoomPreset = (typeof ZOOM_PRESETS)[number]

/** Where the camera is: its zoom (render units per CSS px at the surface under it) and altitude (in globe radii). */
export interface ZoomReadout {
  readonly worldPerCssPixel: number
  readonly altitude: number
}

/** A running war the tuner can retune; the war screen implements it. */
export interface TunableWar {
  applyTuning(values: TuningValues): void
  previewCities(preview: CityPreview): void
  /** Shows palette `mode` whatever the player's setting (the tuner judges both palettes). */
  previewPalette(mode: PaletteMode): void
  zoom(preset: ZoomPreset): void
  readout(): ZoomReadout
}

export interface TuningLink {
  /** The values a war screen starts with. */
  readonly values: TuningValues
  /** A war screen offers itself for live retuning until `signal` aborts (its screen is left). */
  attach(war: TunableWar, signal: AbortSignal): void
}

/** Players' builds: the committed values; nothing retunes the war. */
export function fixedTuning(values: TuningValues): TuningLink {
  return { values, attach: () => {} }
}
